import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import logger from '../../utils/logger';
import { BusinessPartner, BusinessPartnerLocation, BusinessPartnerRole } from '../../types';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import {
  BuildingOffice2Icon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { useTextInput } from '../../hooks/useTextInput';
import { useModal } from '../../hooks/useModal';
import { getCompanyId } from '../../utils/company';
import { isTestEnvironment } from '../../utils/environment';

interface BusinessPartnerSelectorProps {
  value?: {
    partnerId: string;
    locationId?: string;
  };
  onChange: (selection: { partnerId: string; locationId?: string } | null) => void;
  role: BusinessPartnerRole;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  // 自由入力モード（住所入力はCaseFormPage側で実装）
  allowCustomInput?: boolean;
  // 案件ID（使用実績記録用）
  caseId?: string;
  // 編集モード時の現在の取引先ID（CompanyPartnerSelection設定に関係なく選択肢に含める）
  currentPartnerId?: string;
}

interface BusinessPartnerOption {
  partnerId: string;
  locationId?: string;
  displayName: string;
  partner?: BusinessPartner; // OCRオプションの場合はundefined
  location?: BusinessPartnerLocation;
  isOCROption?: boolean; // OCRオプションかどうかのフラグ
}

export const BusinessPartnerSelector: React.FC<BusinessPartnerSelectorProps> = ({
  value,
  onChange,
  role,
  placeholder = '取引先を選択してください',
  label,
  required = false,
  disabled = false,
  className = '',
  allowCustomInput = false,
  caseId,
  currentPartnerId,
}) => {
  // デバッグログ出力ヘルパー（テスト環境では無効化）
  const debugLog = useCallback((message: string, data: any) => {
    if (!isTestEnvironment()) {
      console.warn(message, data);
    }
  }, []);
  const { user } = useAuth();
  const {
    businessPartners,
    getCompanyPartnerSelectionsByCompanyId,
    updateCompanyPartnerUsage,
    getBusinessPartnerById,
  } = useMasterData();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputMode, setInputMode] = useState<'dropdown' | 'custom'>('dropdown');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 統一入力処理フック（検索クエリ用）
  const searchInput = useTextInput({
    initialValue: searchQuery,
    onValueChange: value => {
      setSearchQuery(value);
    },
  });

  // 統一モーダル管理フック（ドロップダウン用）
  useModal({
    isOpen: isOpen,
    onClose: () => {
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue('');
    },
    containerRef: dropdownRef,
    closeOnOverlayClick: true,
    closeOnEscape: true,
  });

  // 認証ユーザーの会社IDを統一的に取得
  const companyId = getCompanyId(user);

  // 役割表示名を取得する関数（他のコードより先に定義）
  const getRoleDisplayName = (role: BusinessPartnerRole) => {
    switch (role) {
      case BusinessPartnerRole.BILLING:
        return '請求先';
      case BusinessPartnerRole.PICKUP:
        return '引取先';
      case BusinessPartnerRole.DELIVERY:
        return '搬入先';
      default:
        return '取引先';
    }
  };

  // デバッグ: 受け取った値を確認
  useEffect(() => {
    debugLog(`🔍 [DEBUG] BusinessPartnerSelector初期化: ${getRoleDisplayName(role)}`, {
      timestamp: new Date().toISOString(),
      props: {
        value,
        role,
        label,
        disabled,
        currentPartnerId,
      },
      context: {
        companyId,
        businessPartnersCount: businessPartners.length,
        businessPartnersData: businessPartners.map(p => ({
          id: p.id,
          name: p.name,
          dataSource: p.dataSource,
          status: p.status,
        })),
        customPartnersCount: businessPartners.filter(p => p.dataSource === 'custom').length,
        systemPartnersCount: businessPartners.filter(p => p.dataSource === 'rakuhai_provided')
          .length,
      },
      masterDataLoaded: {
        hasBusinessPartners: businessPartners.length > 0,
        hasCompanyPartnerSelections: companyPartnerSelections.length > 0,
      },
    });
  }, [value, role, label, disabled, currentPartnerId, companyId, businessPartners, debugLog]);

  // 会社の取引先選択設定を取得
  const companyPartnerSelections = useMemo(() => {
    const selections = getCompanyPartnerSelectionsByCompanyId(companyId);
    debugLog(`🔍 [DEBUG] CompanyPartnerSelections取得: ${getRoleDisplayName(role)}`, {
      companyId,
      selectionsCount: selections.length,
      selectionsData: selections.map(s => ({
        id: s.id,
        businessPartnerId: s.businessPartnerId,
        isEnabled: s.isEnabled,
        billingAllowed: s.billingAllowed,
        pickupAllowed: s.pickupAllowed,
        deliveryAllowed: s.deliveryAllowed,
      })),
      enabledSelectionsCount: selections.filter(s => s.isEnabled).length,
    });
    return selections;
  }, [companyId, getCompanyPartnerSelectionsByCompanyId, role]);

  // currentPartnerIdまたはvalue.partnerIdの取引先情報を取得
  const currentPartner = useMemo(() => {
    const partnerId = currentPartnerId || value?.partnerId;
    if (!partnerId) return null;

    const partner = getBusinessPartnerById(partnerId);
    logger.info(`[BusinessPartnerSelector] 現在の取引先情報取得:`, {
      partnerId,
      found: !!partner,
      name: partner?.name,
      displayName: partner?.displayName,
      source: currentPartnerId ? 'currentPartnerId' : 'value.partnerId',
    });

    return partner;
  }, [currentPartnerId, value?.partnerId, getBusinessPartnerById]);

  // 役割に応じてフィルタリングされたBusinessPartnerオプションを生成
  const partnerOptions = useMemo((): BusinessPartnerOption[] => {
    const options: BusinessPartnerOption[] = [];

    // FL-67: 引取先・搬入先にOCRオプションを追加（請求先は住所不要なので除外）
    if (role === BusinessPartnerRole.PICKUP || role === BusinessPartnerRole.DELIVERY) {
      const roleDisplayName = getRoleDisplayName(role);
      options.push({
        partnerId: 'ocr-reader',
        displayName: `📷 画像から${roleDisplayName}を読み取り`,
        isOCROption: true,
      });
    }

    businessPartners
      .filter(partner => {
        const debugInfo = {
          partnerId: partner.id,
          partnerName: partner.name,
          dataSource: partner.dataSource,
          status: partner.status,
          availableRoles: partner.availableRoles,
          requestedRole: role,
          currentPartnerId,
          isCurrentPartner: currentPartnerId === partner.id,
        };

        debugLog(`🔍 [DEBUG] フィルタ判定開始: ${partner.name}`, debugInfo);

        // 編集モード時: 現在選択されている取引先は設定に関係なく常に含める
        if (currentPartnerId && partner.id === currentPartnerId) {
          debugLog(`✅ [DEBUG] 強制採用: ${partner.name}`, {
            ...debugInfo,
            reason: '編集モードの現在取引先として強制的に含める',
          });
          return true;
        }

        // 基本的なフィルタリング: アクティブかどうかをチェック（未設定は後方互換として有効扱い）
        const isActive = partner.status === undefined || partner.status === 'Active';
        if (!isActive) {
          debugLog(`🚫 [DEBUG] 除外: ${partner.name}`, {
            ...debugInfo,
            reason: `ステータスが非アクティブ (${partner.status})`,
          });
          return false;
        }

        // availableRolesチェック（空配列の場合は全ての役割を許可）
        const hasAvailableRoles = partner.availableRoles && partner.availableRoles.length > 0;
        if (hasAvailableRoles) {
          // availableRolesが設定されている場合、該当roleが含まれているかチェック
          if (!partner.availableRoles.includes(role)) {
            debugLog(`🚫 [DEBUG] 除外: ${partner.name}`, {
              ...debugInfo,
              reason: `availableRolesに${getRoleDisplayName(role)}が含まれていない`,
            });
            return false;
          } else {
            debugLog(`✅ [DEBUG] availableRoles通過: ${partner.name}`, {
              ...debugInfo,
              reason: `availableRolesに${getRoleDisplayName(role)}が含まれている`,
            });
          }
        } else {
          // availableRolesが未設定または空配列の場合は全ての役割で利用可能とする（後方互換性）
          debugLog(`✅ [DEBUG] availableRoles未設定通過: ${partner.name}`, {
            ...debugInfo,
            reason: 'availableRoles未設定のため全役割で利用可能（後方互換性）',
          });
        }

        // 全ての取引先（カスタムもシステム提供も）でCompanyPartnerSelection設定に従う
        debugLog(`🔍 [DEBUG] データソース確認: ${partner.name}`, {
          ...debugInfo,
          dataSource: partner.dataSource,
        });

        // CompanyPartnerSelection設定チェック（全取引先対象）
        debugLog(`🔍 [DEBUG] CompanyPartnerSelections確認: ${partner.name}`, {
          ...debugInfo,
          companyPartnerSelectionsLength: companyPartnerSelections.length,
          companyPartnerSelectionsData: companyPartnerSelections.slice(0, 3),
        });

        if (companyPartnerSelections.length === 0) {
          debugLog(`✅ [DEBUG] CompanyPartnerSelection設定なしで採用: ${partner.name}`, {
            ...debugInfo,
            reason: 'CompanyPartnerSelection設定がないため全て表示',
          });
          return true;
        }

        // CompanyPartnerSelection設定がある場合のみ、その設定に従う（システム提供データのみ）
        const partnerSelection = companyPartnerSelections.find(
          selection => selection.businessPartnerId === partner.id
        );

        debugLog(`🔍 [DEBUG] パートナー設定検索結果: ${partner.name}`, {
          ...debugInfo,
          partnerSelection: partnerSelection,
          searchKey: partner.id,
          allBusinessPartnerIds: companyPartnerSelections.map(s => s.businessPartnerId),
        });

        // 該当パートナーの設定がない場合は表示しない（明示的管理モード）
        if (!partnerSelection) {
          debugLog(`🚫 [DEBUG] 除外: ${partner.name}`, {
            ...debugInfo,
            reason: 'CompanyPartnerSelection設定に該当パートナーが見つからない（明示的管理モード）',
          });
          return false;
        }

        // 設定が無効化されている場合は除外
        if (!partnerSelection.isEnabled) {
          debugLog(`🚫 [DEBUG] 除外: ${partner.name}`, {
            ...debugInfo,
            partnerSelection,
            reason: 'CompanyPartnerSelectionで無効化されている',
          });
          return false;
        }

        // 特定の役割が許可されていない場合は除外
        const roleAllowed =
          (role === BusinessPartnerRole.BILLING && partnerSelection.billingAllowed) ||
          (role === BusinessPartnerRole.PICKUP && partnerSelection.pickupAllowed) ||
          (role === BusinessPartnerRole.DELIVERY && partnerSelection.deliveryAllowed);

        debugLog(`🔍 [DEBUG] 役割許可確認: ${partner.name}`, {
          ...debugInfo,
          partnerSelection: {
            billingAllowed: partnerSelection.billingAllowed,
            pickupAllowed: partnerSelection.pickupAllowed,
            deliveryAllowed: partnerSelection.deliveryAllowed,
          },
          roleAllowed,
          roleCheck: {
            isBilling: role === BusinessPartnerRole.BILLING,
            isPickup: role === BusinessPartnerRole.PICKUP,
            isDelivery: role === BusinessPartnerRole.DELIVERY,
          },
        });

        if (!roleAllowed) {
          debugLog(`🚫 [DEBUG] 除外: ${partner.name}`, {
            ...debugInfo,
            partnerSelection,
            reason: `役割${getRoleDisplayName(role)}が許可されていない`,
          });
          return false;
        }

        debugLog(`✅ [DEBUG] 最終採用: ${partner.name}`, {
          ...debugInfo,
          partnerSelection,
          reason: '全てのフィルタリング条件を通過',
        });
        return true;
      })
      .forEach(partner => {
        logger.info(`[BusinessPartnerSelector] オプション生成開始: ${partner.name}`, {
          role: getRoleDisplayName(role),
          locationsCount: partner.locations?.length || 0,
          locations:
            partner.locations?.map(loc => ({
              id: loc.id,
              name: loc.name,
              roles: loc.roles,
              isDefault: loc.isDefault,
            })) || 'no locations',
        });

        // 該当する役割を持つ拠点のみを抽出
        const relevantLocations =
          partner.locations?.filter(location => (location.roles || []).includes(role)) || [];

        logger.info(`[BusinessPartnerSelector] ${partner.name} - 役割フィルタ後`, {
          relevantLocationsCount: relevantLocations.length,
          relevantLocations: relevantLocations.map(loc => ({
            id: loc.id,
            name: loc.name,
            roles: loc.roles,
          })),
        });

        if (relevantLocations.length === 0) {
          // 役割を持つ拠点がない場合はデフォルト拠点を使用
          const defaultLocation = partner.locations?.find(loc => loc.isDefault);

          if (defaultLocation) {
            options.push({
              partnerId: partner.id,
              locationId: undefined, // デフォルト拠点の場合はlocationIdを省略
              displayName: partner.displayName || partner.name,
              partner,
              location: defaultLocation,
            });
            logger.info(
              `[BusinessPartnerSelector] ${partner.name} - デフォルト拠点でオプション追加完了`
            );
          } else {
            // 拠点情報が全くない場合でも、取引先自体をオプションとして追加
            options.push({
              partnerId: partner.id,
              locationId: undefined,
              displayName: partner.displayName || partner.name,
              partner,
              location: undefined, // 拠点情報なし
            });
            logger.info(
              `[BusinessPartnerSelector] ${partner.name} - 拠点情報なし、取引先のみでオプション追加`
            );
          }
        } else {
          // 役割を持つ各拠点をオプションとして追加
          relevantLocations.forEach(location => {
            const displayName = location.isDefault
              ? partner.displayName || partner.name
              : `${partner.displayName || partner.name} ${location.name}`;

            options.push({
              partnerId: partner.id,
              locationId: location.isDefault ? undefined : location.id,
              displayName,
              partner,
              location,
            });
            logger.info(
              `[BusinessPartnerSelector] ${partner.name} - 拠点 ${location.name} でオプション追加完了`
            );
          });
        }
      });

    // OCRオプションを先頭に、他は名前順にソート
    const sortedOptions = options.sort((a, b) => {
      if (a.isOCROption && !b.isOCROption) return -1;
      if (!a.isOCROption && b.isOCROption) return 1;
      return a.displayName.localeCompare(b.displayName);
    });

    logger.info(
      `[BusinessPartnerSelector] ${getRoleDisplayName(role)}用オプション数: ${sortedOptions.length}`,
      sortedOptions.map(o => ({ partnerId: o.partnerId, displayName: o.displayName }))
    );

    return sortedOptions;
  }, [businessPartners, role, companyPartnerSelections, currentPartnerId]);

  // 検索フィルタリング
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return partnerOptions;

    const query = searchQuery.toLowerCase();
    return partnerOptions.filter(
      option =>
        option.displayName.toLowerCase().includes(query) ||
        option.partner?.name.toLowerCase().includes(query) ||
        option.location?.address.toLowerCase().includes(query)
    );
  }, [partnerOptions, searchQuery]);

  // 現在選択されているオプションを取得
  const selectedOption = useMemo(() => {
    if (!value) {
      logger.info(`[BusinessPartnerSelector] ${getRoleDisplayName(role)}: valueなし`);
      return null;
    }

    const found = partnerOptions.find(option => {
      const partnerIdMatch = option.partnerId === value.partnerId;
      // locationIdの比較: undefined, null, 空文字列を同一視
      const valueLocationId = value.locationId || undefined;
      const optionLocationId = option.locationId || undefined;
      const locationIdMatch = valueLocationId === optionLocationId;

      logger.info(`[BusinessPartnerSelector] オプション比較: ${option.displayName}`, {
        partnerIdMatch,
        locationIdMatch,
        valueLocationId,
        optionLocationId,
        match: partnerIdMatch && locationIdMatch,
      });

      return partnerIdMatch && locationIdMatch;
    });

    // 詳細なデバッグログ（見つからない場合の原因を特定）
    if (!found && value.partnerId) {
      logger.info(
        `[BusinessPartnerSelector] ${getRoleDisplayName(role)}: 選択肢に見つからない詳細分析`,
        {
          searchingFor: { partnerId: value.partnerId, locationId: value.locationId || undefined },
          allOptions: partnerOptions.map(o => ({
            partnerId: o.partnerId,
            locationId: o.locationId || undefined,
            displayName: o.displayName,
          })),
          currentPartnerExists: !!currentPartner,
          currentPartnerInfo: currentPartner
            ? {
                id: currentPartner.id,
                name: currentPartner.name,
                displayName: currentPartner.displayName,
              }
            : null,
        }
      );
    }

    logger.info(`[BusinessPartnerSelector] ${getRoleDisplayName(role)}: 選択状態確認`, {
      value,
      found: found ? { partnerId: found.partnerId, displayName: found.displayName } : 'NOT FOUND',
      totalPartnerOptions: partnerOptions.length,
      sampleOptions: partnerOptions
        .slice(0, 3)
        .map(o => ({ partnerId: o.partnerId, displayName: o.displayName })),
      hasCurrentPartner: !!currentPartner,
      currentPartnerName: currentPartner?.displayName || currentPartner?.name,
    });

    return found;
  }, [value, partnerOptions, role]);

  const handleOptionSelect = useCallback(
    (option: BusinessPartnerOption) => {
      onChange({
        partnerId: option.partnerId,
        locationId: option.locationId,
      });
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue(''); // 統一入力フックもリセット

      // 使用実績を記録（OCRオプション以外）
      if (!option.isOCROption && option.partnerId !== 'ocr-reader') {
        try {
          updateCompanyPartnerUsage(option.partnerId, 1);
        } catch (error) {
          logger.warn('使用実績の記録に失敗しました:', error);
          // エラーが発生しても選択処理は継続
        }
      }
    },
    [onChange, companyId, role, updateCompanyPartnerUsage, caseId, searchInput]
  );

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
    setSearchQuery('');
    searchInput.setValue(''); // 統一入力フックもリセット
  };

  // 入力モード切り替え処理
  const handleModeSwitch = useCallback(
    (mode: 'dropdown' | 'custom') => {
      setInputMode(mode);
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue(''); // 検索フックリセット
      if (mode === 'dropdown') {
        onChange(null);
      }
    },
    [onChange, searchInput]
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {label && (
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      {label && (
        // アクセシビリティ/テスト用のネイティブセレクト（非表示）
        <select
          aria-label={label || getRoleDisplayName(role)}
          className='sr-only'
          value={value?.partnerId || ''}
          onChange={e => {
            const selectedPartnerId = e.target.value;
            const option = partnerOptions.find(o => o.partnerId === selectedPartnerId);
            if (option) {
              handleOptionSelect(option);
            } else if (selectedPartnerId === '') {
              handleClear();
            }
          }}
          disabled={disabled}
          data-testid={`bp-hidden-select-${role}`}
        >
          <option value=''>選択してください</option>
          {partnerOptions
            .filter(o => !o.isOCROption)
            .map(o => (
              <option key={`${o.partnerId}`} value={o.partnerId}>
                {/* 非表示セレクトはテキスト検索に引っかからないよう空文字にする */}
              </option>
            ))}
        </select>
      )}

      {/* 入力モード切り替えボタン（自由入力が有効な場合のみ表示） */}
      {allowCustomInput && (
        <div className='flex space-x-2'>
          <Button
            type='button'
            variant={inputMode === 'dropdown' ? 'primary' : 'secondary'}
            size='sm'
            onClick={() => handleModeSwitch('dropdown')}
            disabled={disabled}
          >
            取引先選択
          </Button>
          <Button
            type='button'
            variant={inputMode === 'custom' ? 'primary' : 'secondary'}
            size='sm'
            onClick={() => handleModeSwitch('custom')}
            disabled={disabled}
            data-testid={`${role.toLowerCase()}-partner-free-input-button`}
          >
            自由入力
          </Button>
        </div>
      )}

      {/* プルダウン選択モード */}
      {inputMode === 'dropdown' && (
        <div className='relative' ref={dropdownRef}>
          {/* セレクタートリガー */}
          <div className='relative'>
            <button
              type='button'
              onClick={() => !disabled && setIsOpen(!isOpen)}
              disabled={disabled}
              className={`
                          w-full px-4 py-3 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 
                          rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                          ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
                          transition-colors duration-200
                      `}
            >
              <div className='flex items-center justify-between'>
                <div className='flex items-center'>
                  <BuildingOffice2Icon className='h-5 w-5 text-gray-400 mr-3' />
                  <span
                    className={
                      selectedOption || currentPartner
                        ? 'text-gray-900 dark:text-white'
                        : 'text-gray-500 dark:text-gray-400'
                    }
                  >
                    {(() => {
                      // 表示名の決定（優先順位: selectedOption > currentPartner > placeholder）
                      if (selectedOption) {
                        return selectedOption.displayName;
                      }
                      if (currentPartner) {
                        const fallbackName = currentPartner.displayName || currentPartner.name;
                        return fallbackName;
                      }
                      return placeholder;
                    })()}
                  </span>
                </div>
                <ChevronDownIcon
                  className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </div>
            </button>
          </div>

          {/* ドロップダウンメニュー */}
          {isOpen && !disabled && (
            <div className='absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden'>
              {/* 検索ボックス */}
              <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
                <div className='relative'>
                  <MagnifyingGlassIcon className='h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2' />
                  <input
                    type='text'
                    {...searchInput.inputProps}
                    placeholder={`${getRoleDisplayName(role)}を検索...`}
                    className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                  />
                </div>
              </div>

              {/* オプションリスト */}
              <div className='max-h-64 overflow-y-auto'>
                {/* クリアオプション */}
                {(selectedOption || currentPartner) && (
                  <button
                    type='button'
                    onClick={handleClear}
                    className='w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                  >
                    選択をクリア
                  </button>
                )}

                {/* 取引先オプション */}
                {filteredOptions.length > 0 ? (
                  filteredOptions.map(option => (
                    <button
                      key={`${option.partnerId}-${option.locationId || 'default'}`}
                      type='button'
                      onClick={() => handleOptionSelect(option)}
                      className={`
                                        w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                                        ${
                                          selectedOption?.partnerId === option.partnerId &&
                                          selectedOption?.locationId === option.locationId
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                            : 'text-gray-900 dark:text-white'
                                        }
                                    `}
                    >
                      <div className='flex items-center'>
                        {option.isOCROption ? (
                          <PhotoIcon className='h-4 w-4 text-blue-500 mr-3' />
                        ) : (
                          <BuildingOffice2Icon className='h-4 w-4 text-gray-400 mr-3' />
                        )}
                        <div>
                          <div
                            className={`font-medium ${option.isOCROption ? 'text-blue-600 dark:text-blue-400' : ''}`}
                          >
                            {option.displayName}
                          </div>
                          {option.location && (
                            <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                              {option.location.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className='px-4 py-3 text-gray-500 dark:text-gray-400 text-center'>
                    {searchQuery
                      ? '検索結果が見つかりません'
                      : `利用可能な${getRoleDisplayName(role)}がありません`}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 注: 自由入力モードの住所入力はCaseFormPage側で「引取住所」「搬入住所」フィールドで実装 */}
    </div>
  );
};
